import React, { memo, useCallback, useMemo } from 'react';

import { Box, Button, CircularProgress, Drawer, Typography } from '@mui/material';
import { CancelIcon, NoteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { formatGuidelineCitationText } from 'constants/aiChat';

import { AiChatTestIds } from '../../AiChat.testIds';

import {
  closeButtonStyles,
  closeIconStyles,
  containerStyles,
  contentStyles,
  drawerPaperProps,
  drawerPaperStyles,
  drawerRootStyles,
  footerStyles,
  guidelineTextStyles,
  headerLeftStyles,
  headerStyles,
  headerTextContainerStyles,
  iconStyles,
  loadingContainerStyles,
  sectionTextStyles,
  sourceTextStyles,
  titleTextStyles,
} from './GuidelineFullView.styles';

const GuidelineFullView = ({
  isOpen,
  citation,
  fullContent,
  isLoading,
  onClose,
  onAskAboutGuideline,
  isAskHidden = false,
  zIndex = null,
}) => {
  const handleAskAboutGuideline = useCallback(() => {
    onAskAboutGuideline?.(citation);
    onClose?.();
  }, [citation, onAskAboutGuideline, onClose]);

  // A caller can lift the drawer above a higher-stacked surface (the AI Activity
  // run drawer sits at a larger z-index than this drawer's chat-tuned default),
  // otherwise the styles' own z-index applies.
  const rootSx = useMemo(
    () => (zIndex ? { ...drawerRootStyles, zIndex } : drawerRootStyles),
    [zIndex],
  );
  const paperProps = useMemo(
    () => (zIndex ? { sx: { ...drawerPaperStyles, zIndex } } : drawerPaperProps),
    [zIndex],
  );

  if (!citation) {
    return null;
  }

  const citationText = formatGuidelineCitationText(citation);

  return (
    <Drawer
      PaperProps={paperProps}
      anchor="right"
      data-testid={AiChatTestIds.GUIDELINE_FULL_VIEW_DRAWER}
      open={isOpen}
      sx={rootSx}
      onClose={onClose}
    >
      <Box sx={containerStyles}>
        <Box sx={headerStyles}>
          <Box sx={headerLeftStyles}>
            <NoteIcon style={iconStyles} />
            <Box sx={headerTextContainerStyles}>
              <Typography sx={sourceTextStyles} variant="regular12">
                {citation.source}
              </Typography>
              <Typography sx={sectionTextStyles} variant="medium14">
                {citationText}
              </Typography>
              <Typography sx={titleTextStyles} variant="regular14">
                {citation.title}
              </Typography>
            </Box>
          </Box>
          <Box
            aria-label="Close guideline view"
            role="button"
            sx={closeButtonStyles}
            tabIndex={0}
            onClick={onClose}
          >
            <CancelIcon size={20} style={closeIconStyles} />
          </Box>
        </Box>

        <Box sx={contentStyles}>
          {isLoading ? (
            <Box sx={loadingContainerStyles}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Typography sx={guidelineTextStyles} variant="regular14">
              {fullContent || citation.excerpt}
            </Typography>
          )}
        </Box>

        <Box sx={footerStyles}>
          {!isAskHidden && (
            <Button
              data-testid={AiChatTestIds.GUIDELINE_FULL_VIEW_ASK_BUTTON}
              size="small"
              variant="secondary"
              onClick={handleAskAboutGuideline}
            >
              Ask About This Guideline
            </Button>
          )}
          <Button
            data-testid={AiChatTestIds.GUIDELINE_FULL_VIEW_CLOSE_BUTTON}
            size="small"
            variant="tertiary"
            onClick={onClose}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

GuidelineFullView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  citation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
  }),
  fullContent: PropTypes.string,
  isLoading: PropTypes.bool,
  isAskHidden: PropTypes.bool,
  zIndex: PropTypes.number,
  onClose: PropTypes.func,
  onAskAboutGuideline: PropTypes.func,
};

export default memo(GuidelineFullView);
